import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import useUiuxStore, { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';

import { createWMS1141_R01LotNo, deleteWMS1141_R01, downloadExcel_WMS1141_R01, getPdfBlob_WMS1141_R01, getWMS1141_R01AllCount, getWMS1141_R01DetailList, getWMS1141_R01List, getWMS1141_R01One, postWMS1141_R01 } from "./Api_WMS1141_R01";
import { defaultWMS1141_R01DetailDto, defaultWMS1141_R01HeaderDto, WMS1141_R01DetailDto, WMS1141_R01Dto, WMS1141_R01HeaderDto } from './Dto_WMS1141_R01';
import { AgGridReact } from 'ag-grid-react';
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler, InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { validateString } from 'src/utils/string/stringUtil';
import { validateCustomCd, validateEmployeeCd } from 'src/utils/grid/GridValidateContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	WMS1141_R01_DETAIL_ROW_VALIDATE,
	WMS1141_R01_HEADER_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import { useWMS1141_R01Store, WMS1141_R01Filter } from './Store_WMS1141_R01';
import { LabelPrintData } from 'src/apis/labelApi';
import { WMS1141_R01_extractUnprintedLabelData } from './labelDataBuilder_WMS1141_R01';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { getDefaultLabelPrintAuto } from 'src/utils/label/labelPrintAutoProgramUtil';
import qz from 'qz-tray';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';

/** WMS1141 입고구분: 구매입고(110), 사급자재입고(170) */
const WMS1141_INOUT_CLASS_MINOR_CDS = ['110', '170'] as const;

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1141_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<WMS1141_R01DetailDto> | null>;

	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
	
	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
	
}



export const Content_WMS1141_R01 = (props: Props) => {

	const {
		gridRef, detailGridRef,
		tabList, setMessage,
		containerRef,
		isResizing,
	} = props;

	const { vatTypeList, moneyCdList, itemStatusList, inoutClassList, inoutMethodList, inoutTypeList, wmsCdList, compCdList, customCdList, employeeCdList, warehouseCdList, programList } = useCommonCodeStore();

	const defaultLabelPrintAuto = useMemo(
		() => getDefaultLabelPrintAuto(programList),
		[programList]
	);
	const {
		WMS1141_R01Filter, setWMS1141_R01Filter,
		WMS1141_R01PastFilter, setWMS1141_R01PastFilter,
		WMS1141_R01List, setWMS1141_R01List,
		editWMS1141_R01, setEditWMS1141_R01,
		deletedWMS1141_R01DetailList, setDeletedWMS1141_R01DetailList, addDeletedWMS1141_R01DetailList, removeDeletedWMS1141_R01DetailList,
		WMS1141_R01DetailList, setWMS1141_R01DetailList, insertWMS1141_R01DetailList,
		selectedWMS1141_R01, setSelectedWMS1141_R01, initSelectedWMS1141_R01,
		selectedWMS1141_R01DetailList, setSelectedWMS1141_R01DetailList, addSelectedWMS1141_R01DetailList, removeSelectedWMS1141_R01DetailList, initSelectedWMS1141_R01DetailList,
		originalWMS1141_R01DetailList, setOriginalWMS1141_R01DetailList,
		allCount, setAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData, 
		setScrollTop,
		setDetailScrollTop,
	} = useWMS1141_R01Store();
	const { PUR1020_WMS1141NavigationData, setPUR1020_WMS1141NavigationData } = useUiuxStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { printBulkLabel, printPdfFromBlob } = useQzPrint();

	useFetchActionbarAuth("WMS1141_R01");

	const [clickable, setClickable] = useState(true);

	const prevWmsOrderNoRef = useRef<string | undefined>(undefined);

	const handlePrintClick = useCallback(async () => {
		if (!editWMS1141_R01 || !editWMS1141_R01.transportNo) {
				showWarning("출력할 수불번호를 선택하세요.");
				setMessage("출력할 수불번호를 선택하세요.");
				return;
		}
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_WMS1141_R01(editWMS1141_R01.transportNo);
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [editWMS1141_R01, printPdfFromBlob, setLoadingSpinner, setMessage]);
	

	const handleExcelClick = useCallback(async () => {
		if (!editWMS1141_R01) {
			showWarning("선택된 수불내역이 없습니다.");
			return;
		}
		setLoadingSpinner(true);

		const response = await downloadExcel_WMS1141_R01('', editWMS1141_R01.transportNo, WMS1141_R01DetailList.length ?? 0);

		downloadSettingExcel({ responseData: response.data.data, fileName: '수불전표 등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [editWMS1141_R01]);
	
	const getParams = (filter: WMS1141_R01Filter) => {
		const params: Partial<WMS1141_R01Filter> = {
			dateRange: filter.dateRange || undefined,
			wmsOrderNo: filter.wmsOrderNo || undefined,
			customCd: filter.customCd || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getWMS1141_R01AllCount(getParams(WMS1141_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [WMS1141_R01Filter, setAllCount]);

	const fetchData = useCallback(async (skipSelection?: boolean) => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);
	
		const responseData = (await getWMS1141_R01List(
			'',
			getParams(WMS1141_R01Filter),
			WMS1141_R01List.length ?? 0
		));

		let newList = [...responseData.data.data ] as WMS1141_R01HeaderDto[];
		setWMS1141_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			if (skipSelection !== true) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedWMS1141_R01,
					() => true,
				);
			}
			setWMS1141_R01PastFilter(WMS1141_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		}else {
			showWarning('데이터가 없습니다.');
			handleCreateClick();
		}
		setLoadingSpinner(false);
		return responseData;
	}, [WMS1141_R01Filter, WMS1141_R01List, setWMS1141_R01List, setWMS1141_R01PastFilter, setSelectedWMS1141_R01, selectedWMS1141_R01, setScrollTop, setDetailScrollTop])

	const fetchOne = useCallback(async () => {
		if (!selectedWMS1141_R01) return;

		const response = await getWMS1141_R01One(selectedWMS1141_R01.transportNo);

		return response.data.data as WMS1141_R01HeaderDto;
	}, [selectedWMS1141_R01]);

	/** 입고구분: minorCd 110(구매입고), 170(사급자재입고)만 노출 */
	const getWms1141InoutClassList = useCallback((): SysCodeDto[] => {
		return inoutClassList.filter((item) =>
			(WMS1141_INOUT_CLASS_MINOR_CDS as readonly string[]).includes(item.minorCd)
		);
	}, [inoutClassList]);

	const defaultWMS1141_R01HeaderRow = useCallback(() => {
		const logicCenter = editWMS1141_R01?.logicCenter || wmsCdList[0]?.minorCd || '';
		const warehouseCdListToUse = warehouseCdList.filter(value =>
			value.logicCenter === logicCenter && value.materialYn === 'Y'
		);
		const inoutClassListToUse = getWms1141InoutClassList();

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(WMS1141_R01_HEADER_ROW_VALIDATE, {
				compCdList,
				inoutClassList: inoutClassListToUse,
				inoutMethodList,
				wmsCdList,
				warehouseCdList: warehouseCdListToUse,
				inoutTypeList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultWMS1141_R01HeaderDto };
		}

		return {
			...defaultWMS1141_R01HeaderDto,
			compCd: compCdList[0].compCd,
			inoutMethod: inoutMethodList[0].minorCd,
			logicCenter,
			warehouseCd: warehouseCdListToUse[0].warehouseCd,
			inoutType: inoutTypeList.find(value => value.minorCd === 'IN')?.minorCd || '',
			inoutClass: inoutClassListToUse[0].minorCd,
		};
	}, [
		editWMS1141_R01, defaultWMS1141_R01HeaderDto, compCdList, wmsCdList, warehouseCdList,
		inoutMethodList, getWms1141InoutClassList, validateCommonCodeLists,
	]);

	const defaultWMS1141_R01DetailRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(WMS1141_R01_DETAIL_ROW_VALIDATE, {
				itemStatusList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultWMS1141_R01DetailDto };
		}

		return {
			...defaultWMS1141_R01DetailDto,
			itemStatus: itemStatusList[0].minorCd,
			printed: false,
			inserted: true,
		};
	}, [defaultWMS1141_R01DetailDto, itemStatusList, validateCommonCodeLists]);
	
	const initContainerData = useCallback(() => {
		initData();
		setWMS1141_R01Filter({ labelPrintAuto: defaultLabelPrintAuto });
		removeDeletedWMS1141_R01DetailList();
		removeSelectedWMS1141_R01DetailList();
		setEditWMS1141_R01({ ...defaultWMS1141_R01HeaderRow(), })
	}, [initData, defaultLabelPrintAuto, setWMS1141_R01Filter, removeDeletedWMS1141_R01DetailList, removeSelectedWMS1141_R01DetailList, setEditWMS1141_R01, defaultWMS1141_R01HeaderRow])

	const handleDeleteClick = useCallback(() => {

		if (deletedWMS1141_R01DetailList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedWMS1141_R01DetailList(deletedWMS1141_R01DetailList);
			removeSelectedWMS1141_R01DetailList();
			setWMS1141_R01DetailList([...WMS1141_R01DetailList]);
		} else if (selectedWMS1141_R01DetailList.length > 0) {
			addDeletedWMS1141_R01DetailList(selectedWMS1141_R01DetailList);
			removeSelectedWMS1141_R01DetailList(selectedWMS1141_R01DetailList);
		}
	}, [selectedWMS1141_R01DetailList, 
		deletedWMS1141_R01DetailList,
		addDeletedWMS1141_R01DetailList,
		removeDeletedWMS1141_R01DetailList,
		removeSelectedWMS1141_R01DetailList,
		setWMS1141_R01DetailList,
		WMS1141_R01DetailList]);
	
	const handleCreateClick = async () => {
		setEditWMS1141_R01({ ...defaultWMS1141_R01HeaderRow(), })
		setSelectedWMS1141_R01(undefined);
		setDetailFetchTrigger(false);
		setWMS1141_R01DetailList([]);
		await insertWMS1141_R01DetailList([{...defaultWMS1141_R01DetailRow()}]);
		setDeletedWMS1141_R01DetailList([]);
		setSelectedWMS1141_R01DetailList([]);
		setOriginalWMS1141_R01DetailList([]);
	}

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: WMS1141_R01DetailList,
			rowData: isGridInsert ? WMS1141_R01DetailList[rowIndex] : { ...defaultWMS1141_R01DetailRow(), valueIndex: -1 },
			insertRow: insertWMS1141_R01DetailList,
			defaultRowData: defaultWMS1141_R01DetailRow(),
			...(isGridInsert && { setClickable }),
		});
	}, [WMS1141_R01DetailList, insertWMS1141_R01DetailList, defaultWMS1141_R01DetailRow, setClickable]);

	const validateInsertData = (header: WMS1141_R01HeaderDto, detailList: WMS1141_R01DetailDto[]) => {

		if (!validateString(header.compCd)) {
			showWarning('사업장을 선택해주세요.'); return false;
		}
		if (!validateString(header.customCd)) {
			showWarning('거래처를 입력해주세요.'); return false;
		}
		if (!validateCustomCd(header.customCd, customCdList)) {
			showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
		if (!validateString(header.logicCenter)) {
			showWarning('물류센터를 선택해주세요.'); return false;
		}
		if (!validateString(header.warehouseCd)) {
			showWarning('창고번호를 선택해주세요.'); return false;
		}
		// if (!validateString(header.empNo)) {
		// 	showWarning('입고담당자를 선택해주세요.'); return false;
		// }
		// if (!validateEmployeeCd(header.empNo, employeeCdList)) {
		// 	showWarning('입고담당자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		// }
		if(!validateString(header.inoutMethod)) {
			showWarning('입고구분을 선택해주세요.'); return false;
		}

		// 품목코드 중복 체크 (삭제 표시된 행 제외)
		if(detailList.length === 0) {
			showWarning('상세내역을 입력해주세요.'); return false;
		}
		for(const item of detailList) {
			if (deletedWMS1141_R01DetailList.includes(item.valueIndex.toString())) {
				continue;
			}
			if(!validateString(item.itemCd)) {
				showWarning('품목코드를 입력해주세요.'); return false;
			}
			if(!validateString(item.itemStatus)) {
				showWarning('품목의 상태를 선택해주세요.'); return false;
			}
			if(item.qty <= 0) {
				showWarning('수량을 입력해주세요.'); return false;
			}

			const activeDetailList = detailList.filter(detail => !deletedWMS1141_R01DetailList.includes(detail.valueIndex.toString()));
			const otherDetailList = activeDetailList.filter((_, index) => index !== detailList.findIndex(detail => detail.itemCd === _.itemCd));
			if (otherDetailList.some(otherDetail => otherDetail.itemCd === item.itemCd)) {
				showWarning("중복된 품목코드가 있습니다.\n품목코드: " + item.itemCd);
				return false;
			}
		}

		return true;
	}

	const setInfoDetailList = useCallback((header: WMS1141_R01HeaderDto, detailList: WMS1141_R01DetailDto[]) => {
		
		return detailList.map((value, idx) => ({
			...value,
			compCd: header.compCd,
			customCd: header.customCd,
		}))
	}, [setWMS1141_R01DetailList])


	const assignLotNoToMaps = useCallback(async (currentLotList: WMS1141_R01DetailDto[]) => {
		const newLotList = currentLotList.map(detail => ({ ...detail }));
		const promises: Promise<void>[] = [];

		for (const detail of newLotList) {
			if (!validateString(detail.lotNo)) {
				const promise = createWMS1141_R01LotNo().then((res) => {
						if (res.data && res.data.successFlag) {
							detail.lotNo = res.data.data;
						}
					});
					promises.push(promise);
				}
		}

		if (promises.length > 0) {
			await Promise.all(promises);
			setWMS1141_R01DetailList(newLotList);
		}

		return newLotList;
	}, [setWMS1141_R01DetailList]);
	
	const handleSaveClick = useCallback(async () => {

		setLoadingSpinner(true);

		let valid = true;
		if(!validateInsertData(editWMS1141_R01!, WMS1141_R01DetailList)) valid = false;      
		if(!valid) { setLoadingSpinner(false); return; }

		const isExistingTransport = validateString(editWMS1141_R01?.transportNo || '')
			&& WMS1141_R01List.some(row => row.transportNo === editWMS1141_R01?.transportNo);
		if (isExistingTransport) {
			showWarning('저장된 입고정보는 수정할 수 없습니다.  등록버튼을 눌러 신규 등록해주세요.');
			setMessage('저장된 입고정보는 수정할 수 없습니다.');
			setTimeout(() => setMessage(''), 3000);
			setLoadingSpinner(false);
			return;
		}

		setLoadingSpinner(true);
		
		const deletedWMS1141_R01DetailList_Number = deletedWMS1141_R01DetailList.map(value => Number(value));
		let processName = '';
		let newDetailList: WMS1141_R01DetailDto[] = [];
		let newHeaderList: WMS1141_R01HeaderDto[] = [];
		let responseData: WMS1141_R01Dto = {
			wmsTransportHeaderDto: editWMS1141_R01!,
			wmsTransportDetailDtoList: WMS1141_R01DetailList
		};

		// STEP 0: 초기화 전에 LOT 번호 가져와서 WMS1141_R01DetailList의 각 row에 추가
		// ========================================
		newDetailList = await assignLotNoToMaps(WMS1141_R01DetailList);
		// STEP 1: 초기화 전에 미출력 라벨 데이터 추출 - 자동출력 옵션 체크
		// ========================================
		let unprintedLabelData: LabelPrintData[] = [];
		if(WMS1141_R01Filter.labelPrintAuto === 'Y') {
			const store = useWMS1141_R01Store.getState();
			unprintedLabelData = WMS1141_R01_extractUnprintedLabelData(
				store,
				deletedWMS1141_R01DetailList
			);
		}

		try {
				const payload: WMS1141_R01Dto = {
					wmsTransportHeaderDto: editWMS1141_R01!,
					wmsTransportDetailDtoList: setInfoDetailList(editWMS1141_R01!, newDetailList)
				};

				processName = 'post';
				const postResponse = await postWMS1141_R01(payload);
				responseData = postResponse.data.data;
				

				if (postResponse.data.successFlag) {
					showSuccess('사급자재가 입고 되었습니다.');
					setMessage('사급자재가 입고 되었습니다.');
				}else{
					showWarning('사급자재 입고 후 서버로부터 올바른 데이터를 받지 못했습니다.');
					setLoadingSpinner(false);
					return;
				}
			
		} catch (error: any) {
			console.log('error :', error.response?.data?.message || error.message);
			showError(error.response?.data?.message || '사급자재 입고 작업 중 오류가 발생했습니다.');
			setLoadingSpinner(false);
			return;
		}

		if (processName === 'post') {
			newHeaderList = [
				...WMS1141_R01List,
				responseData.wmsTransportHeaderDto,
			] as WMS1141_R01HeaderDto[];
			setWMS1141_R01List(newHeaderList);
			setSelectedWMS1141_R01(responseData.wmsTransportHeaderDto);
		}
		

		// setSelectedWMS1141_R01(undefined);
		// setSelectedWMS1141_R01DetailList([]); // 저장 후 상세행 선택 상태 초기화
		setTimeout(() => {
			setMessage('');
		}, 5000)
		setLoadingSpinner(false);

		// 저장 후 미출력 라벨 출력 (QZ 사용 시 QZ로, 미사용 시 label-40x25 단일 템플릿으로 브라우저 인쇄)
		if (
			WMS1141_R01Filter.labelPrintAuto === 'Y'
			&& unprintedLabelData.length > 0 && processName !== 'delete'
		) {
			try {
				const ok = await printBulkLabel(unprintedLabelData, 'label1');
				if (ok) {
					showSuccess(`저장 완료! ${unprintedLabelData.length}개 라벨 출력`);
				} else {
					showWarning('저장은 완료되었으나 라벨 출력에 실패했습니다.  프린터 설정을 확인해주세요.');
					setLoadingSpinner(false);
				}
			} catch (error: any) {
				console.error('라벨 출력 실패:', error);
				showWarning(`저장은 완료되었으나 라벨 출력에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
				setLoadingSpinner(false);
			}
		}
	}, [
		WMS1141_R01Filter,
		editWMS1141_R01,
		WMS1141_R01DetailList, deletedWMS1141_R01DetailList, setWMS1141_R01DetailList, handleCreateClick,
		printBulkLabel,
	]);

	
	const handleFetchDetailList = useCallback(async (transportNo: string, warehouseCd: string, isNewSelection: boolean) => {
		if (!warehouseCd) {
			showError('수불 정보의 물류정보의 조건에 맞는 창고 코드를 찾을 수 없어 입고 목록 내역을 조회할 수 없습니다.', 5000);
			setWMS1141_R01DetailList([]);
			return;
		}
		// 같은 행 재선택(조회→취소→재조회)에서도 상세는 다시 조회되어야 함.
		// 기존에는 isNewSelection=false 인 경우 조회가 스킵되어 DetailTable이 비는 문제가 있었음.
		// 이전에 선택한 입고정보와 현재 선택한 입고정보가 같으면 상세내역 검색 
        // 중단
        // if(!isNewSelection) return;

		try {
			const detailResponse = await getWMS1141_R01DetailList(transportNo, warehouseCd);
			const detailData = detailResponse.data.data;

			if (detailData && detailData.length > 0) {
				const currentDetailList = useWMS1141_R01Store.getState().WMS1141_R01DetailList;

				const existingItemsMap = new Map<string, WMS1141_R01DetailDto>();
				currentDetailList.forEach(item => {
					if (item.itemCd && item.itemStatus) {
						const key = `${item.itemCd}-${item.itemStatus}`;
						existingItemsMap.set(key, item);
					}
				});

				const updatedDetails = detailData.map((value) => {
					return {
						...value,
						valueIndex: value.valueIndex,
						orderIndex: value.orderIndex,
						orderIndexSeq: value.orderIndexSeq,
						cost: value.cost,
						amt: value.amt,
					}
				});
				setWMS1141_R01DetailList(updatedDetails);
				setOriginalWMS1141_R01DetailList(detailData);
				setMessage(`${selectedWMS1141_R01?.compNm}의 입고지시내역 ${detailData.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);

				// setSelectedWMS1141_R01DetailList([1 as any]);
			} else {
				showError('상세내역이 없습니다.');
				setWMS1141_R01DetailList([]);
			}
		} catch (error) {
			console.error("상세 내역 조회 실패:", error);
			showError('상세 내역을 조회하는 중 오류가 발생했습니다.');
			setWMS1141_R01DetailList([]);
		}
	}, [
		setMessage, 
		setWMS1141_R01DetailList,
		setOriginalWMS1141_R01DetailList,
		selectedWMS1141_R01?.compNm,
	]);
	
	const fetchAllDetailsForSelectedRow = useCallback(async () => {

		if (!selectedWMS1141_R01 || !selectedWMS1141_R01.transportNo) {
			if (!selectedWMS1141_R01) {
				setWMS1141_R01DetailList([]);
				setOriginalWMS1141_R01DetailList([]);
				setDetailFetchTrigger(false);
			}
			prevWmsOrderNoRef.current = undefined; // 이전 선택 초기화
			return;
		}

		// 이전에 선택한 입고정보와 현재 선택한 입고정보가 다른지 확인
		// 다르면 상세 내역 조회 시 입고량 초기화
		const previousOrderNo = prevWmsOrderNoRef.current;
		const currentOrderNo = selectedWMS1141_R01.transportNo;
		
		const isNewSelection = previousOrderNo !== currentOrderNo;
		
		// 1. 입고 정보 먼저 가져오기
		const headerData = await fetchOne();

		// 3. 현재 선택된 행과 API 데이터를 합쳐 기본값을 적용할 베이스 데이터를 만듭니다.
		const baseDataForDefaults = { ...defaultWMS1141_R01HeaderRow(), ...headerData };
		
		//최종적으로 사용할 warehouseCd 결정
		const logicCenterToUse = baseDataForDefaults.logicCenter || '';
		const warehouseCdToUse = baseDataForDefaults.warehouseCd || '';

		// 4. 입고 헤더 정보 업데이트
		setEditWMS1141_R01({
			...defaultWMS1141_R01HeaderRow(),
			...headerData,					// 선택된 행의 정보가 베이스
			logicCenter: logicCenterToUse,
			warehouseCd: warehouseCdToUse,
		});
		
		// 5. 분리된 함수를 호출하여 상세 내역을 등록.
		handleFetchDetailList(selectedWMS1141_R01.transportNo, warehouseCdToUse, isNewSelection);
		prevWmsOrderNoRef.current = selectedWMS1141_R01.transportNo;

	}, [
		detailFetchTrigger,
		selectedWMS1141_R01,
		setWMS1141_R01DetailList,
		setOriginalWMS1141_R01DetailList,
		setDetailFetchTrigger,
		fetchOne,
		defaultWMS1141_R01HeaderRow,
		setEditWMS1141_R01,
		handleFetchDetailList
	]);

	//사급품 입고등록 상세조회 및 네비게이션 데이터 처리 통합
	useEffect(() => {
		const processDetail = async () => {
		// 1. 네비게이션 데이터가 있는 경우 (우선 처리)
		if (PUR1020_WMS1141NavigationData) {
			const navData = PUR1020_WMS1141NavigationData;
			setPUR1020_WMS1141NavigationData(null);

			try{
				// 목록 갱신하되, 첫 번째 행 자동 선택 방지 (skipSelection = true)
				// await fetchData(true);

				// 그리드 선택 해제
				gridRef.current?.api?.deselectAll();

				// 전달받은 데이터로 폼 세팅
				setEditWMS1141_R01({ ...navData.wmsTransportHeaderDto });
				setWMS1141_R01DetailList(navData.wmsTransportDetailDtoList);
				setOriginalWMS1141_R01DetailList(navData.wmsTransportDetailDtoList);

				// 상태 초기화 및 상세조회 트리거 끄기
				useWMS1141_R01Store.setState({
					selectedWMS1141_R01: undefined,
					detailFetchTrigger: false,
					selectedWMS1141_R01DetailList: [],
					deletedWMS1141_R01DetailList: []
				});

			} catch (error) {
				console.error('사급품 입고등록 오류:', error);
				showError('사급품 입고등록 중 오류가 발생했습니다.');
			}

		} else{
			if (!detailFetchTrigger) return;
			fetchAllDetailsForSelectedRow();
			setDetailFetchTrigger(false);
			}
		};

		processDetail();
	}, [fetchAllDetailsForSelectedRow]);

	useEffect(() => {
		setWMS1141_R01Filter({ labelPrintAuto: defaultLabelPrintAuto });
	}, [defaultLabelPrintAuto, setWMS1141_R01Filter]);
	
	return {
		clickable, setClickable,
		handlePrintClick,
		handleExcelClick,
		fetchData,
		getWms1141InoutClassList,
		defaultWMS1141_R01HeaderRow,
		defaultWMS1141_R01DetailRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleInsertClick,
		handleSaveClick,
	};
}
